// PWA install prompt + app badge helpers

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => cb(isInstallable()));
}

export function initInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function onInstallabilityChange(cb) {
  listeners.add(cb);
  cb(isInstallable());
  return () => listeners.delete(cb);
}

export function isInstallable() {
  return Boolean(deferredPrompt);
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

export function getBrowserKind() {
  const ua = navigator.userAgent.toLowerCase();
  if (isIOS()) return "ios-safari";
  if (ua.includes("firefox")) return "firefox";
  if (ua.includes("edg/") || ua.includes("edge")) return "edge";
  if (ua.includes("chrome") || ua.includes("chromium")) return "chrome";
  if (ua.includes("safari")) return "safari";
  return "other";
}

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify();
  return choice;
}

export async function setBadge(count) {
  try {
    if ("setAppBadge" in navigator) {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else if ("clearAppBadge" in navigator) {
        await navigator.clearAppBadge();
      }
    }
    const reg = await navigator.serviceWorker?.ready;
    reg?.active?.postMessage({
      type: count > 0 ? "SET_BADGE" : "CLEAR_BADGE",
      count,
    });
  } catch {
    // best-effort
  }
}

// ============ PUSH NOTIFICATIONS ============

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export async function subscribeToPush(backendUrl) {
  if (!isPushSupported()) throw new Error("Push non supportato");

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Hai negato i permessi. Attivali dalle impostazioni del browser."
        : "Permesso non concesso",
    );
  }

  // Fetch VAPID key
  const resp = await fetch(`${backendUrl}/api/push/vapid-public-key`);
  const { publicKey } = await resp.json();
  if (!publicKey) throw new Error("Chiave VAPID non configurata sul server");

  const registration = await navigator.serviceWorker.ready;

  // Unsubscribe any existing subscription to avoid stale keys
  const existing = await registration.pushManager.getSubscription();
  if (existing) await existing.unsubscribe().catch(() => {});

  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  const body = sub.toJSON();
  body.user_agent = navigator.userAgent;
  await fetch(`${backendUrl}/api/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  return sub;
}

export async function unsubscribeFromPush(backendUrl) {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.getSubscription();
  if (sub) {
    await fetch(`${backendUrl}/api/push/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
    await sub.unsubscribe();
  }
}

export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    return Boolean(sub);
  } catch {
    return false;
  }
}
